import { Icon } from "@indigo-components";
import { FC } from "react";
import type {
  AttachmentMeta,
  PendingAttachment,
} from "~/pages/appointments/hooks/useAgentChat";

/** Width of the conversation rail in panel (foldable) mode. The floating panel
 *  reserves this much extra width when its sidebar is open, and the foldable
 *  rail renders at exactly this width — they must match, so share one constant. */
export const PANEL_SIDEBAR_WIDTH = 220;

/** The italic "Indy" wordmark (Society serif). Shared so the brand typography
 *  lives in one place across the panel title bar, full-screen header, and the
 *  navbar "Ask Indy" label. `color` defaults to `currentColor` (inherits). */
export const IndyWordmark: FC<{ fontSize?: number; color?: string }> = ({
  fontSize,
  color = "currentColor",
}) => (
  <span
    style={{
      fontFamily: "'Society', serif",
      fontWeight: 300,
      fontStyle: "italic",
      fontSize,
      color: color === "currentColor" ? undefined : color,
      WebkitTextStroke: `0.4px ${color}`,
    }}
  >
    Indy
  </span>
);

/** Inline critical layout styles for chat action buttons so they can't be
 *  overridden by Ant Design's CSS cascade. Pairs with the `.chatbot-action-btn`
 *  rule in `IndyChatSurface.css` (see the CSS-Layers TODO there). */
export const ACTION_BTN_STYLE: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 24,
  height: 24,
  border: "none",
  background: "transparent",
  borderRadius: 999,
  cursor: "pointer",
  padding: 0,
};

/**
 * Layout-critical styles for the full-screen header's buttons. Deliberately
 * omits width/height so `.chatbot-touch-btn` can size them per breakpoint
 * (24px chrome on desktop, ~40px tap target on a phone) — an inline size would
 * beat the media query.
 */
export const TOUCH_BTN_STYLE: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  border: "none",
  background: "transparent",
  borderRadius: 999,
  cursor: "pointer",
  padding: 0,
  flexShrink: 0,
};

export const MAX_INPUT_CHARS = 500_000;
/** Mirrors services/agent_chat/models.py: past the threshold the saved row
 *  keeps only a preview and the full text lives in a long_message attachment. */
export const LONG_MESSAGE_THRESHOLD_CHARS = 100_000;
export const LONG_MESSAGE_PREVIEW_CHARS = 100;

export function renderAttachmentIcon(
  attachment: AttachmentMeta,
  size: "default" | "compact" = "default",
) {
  if (attachment.previewUrl) {
    return (
      <img
        src={attachment.previewUrl}
        alt={attachment.filename}
        className="h-full w-full rounded object-cover"
      />
    );
  }

  const iconName =
    attachment.content_type === "application/pdf" ? "pdf" : "file";

  if (size === "compact") {
    return (
      <Icon name={iconName} sizeX={14} fill="var(--indigo-icon-tertiary)" />
    );
  }

  return <Icon name={iconName} size="m" />;
}

/** Icon for a staged composer attachment that has no image preview. */
export function renderPendingAttachmentIcon(attachment: PendingAttachment) {
  return (
    <Icon
      name={attachment.content_type === "application/pdf" ? "pdf" : "file"}
      sizeX={18}
    />
  );
}
